<template>
	<v-overlay :dark="false" :absolute="modal" :class="modal == false ? 'v-overlay-relative' : null">
		<div class="input-modal">
			<div class="input-modal-header">
				<h1 class="input-modal-header-title">Color Options</h1>
				<div v-if="systemCategoryId && itemId" class="d-inline-flex align-center mr-4">
					<SystemCategoryIcon :system-category-id="systemCategoryId" :inventory-item-id="itemId" />
					<span v-if="styleCode">{{ styleCode }}</span>
				</div>
				<div class="form-input">
					<label>Match Previous PO #</label>
					<v-combobox
						outlined
						dense
						hide-details="auto"
						v-model="previousPO"
						:items="purchaseOrders"
						item-text="purchaseOrderNumber"
						:return-object="false"
						:search-input.sync="previousPO"
						@input="(orderNumber) => applyColors(orderNumber)"
					>
						<template v-slot:no-data> </template>
						<template v-slot:item="{ index, item }">
							<span>{{ item.purchaseOrderNumber }} ({{ item.vendor.companyName }})</span>
							<v-spacer></v-spacer>
							<v-list-item-action @click.stop>
								<v-tooltip bottom>
									<template v-slot:activator="{ on, attrs }">
										<v-icon v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
									</template>
									<span>
										<div v-for="(color, idx) in item.orderItems[0].colors" :key="idx">
											<span>
												{{ color.description }} ({{ color.colorOption }}:{{ color.color }})
											</span>
										</div>
									</span>
								</v-tooltip>
							</v-list-item-action>
						</template>
					</v-combobox>
				</div>
				<v-btn outlined dense hide-details="auto" @click="$emit('cancel')"> Cancel </v-btn>
				<v-btn outlined dense hide-details="auto" @click="saveColorOptions()" ref="saveButton"> Save </v-btn>
			</div>
			<div class="input-modal-content">
				<div class="color-container" v-if="isContentHidden">
					<v-row>
						<v-col cols="4">
							<ul class="color-list">
								<li
									v-for="(option, indexOption) in filteredColorOptions"
									:key="option.colorOptionId"
									:class="[
										option.type?.toLowerCase() === 'color'
											? 'color-list-item'
											: 'color-list-item-letters',
										{
											active:
												activeColorOption &&
												activeColorOption.colorOptionId == option.colorOptionId,
										},
									]"
									@click="activeColorOption = option"
								>
									<template v-if="option.type?.toLowerCase() === 'color'">
										<p>{{ `${option.name} - ${option.description}` }}</p>

										<span
											class="color-list-item-preview"
											:class="{
												'color-list-item-preview-active':
													activeColorOption &&
													activeColorOption.colorOptionId == option.colorOptionId,
											}"
											@click="activeColorOption = option"
										>
											<ColorDisplay
												v-if="selected[option.colorOptionId]"
												:hex-code="selected[option.colorOptionId].hexCode"
												:label="selected[option.colorOptionId].label"
												:is-custom-color="selected[option.colorOptionId].isCustomColor"
												shape="circle"
												size="medium"
												style="width: 100%; height: 100%"
											/>
											<v-tooltip
												bottom
												v-if="
													selected[option.colorOptionId] && selected[option.colorOptionId].upCharge
												"
											>
												<template v-slot:activator="{ on, attrs }">
													<span
														v-bind="attrs"
														v-on="on"
														style="display: block; width: 100%; height: 100%; position: absolute; top: 0; left: 0"
													>
														&nbsp;
													</span>
												</template>
												<span>{{
													`Upcharge: ${formatter.format(selected[option.colorOptionId].upCharge)}`
												}}</span>
											</v-tooltip>
											<div
												v-if="selected[option.colorOptionId] && selected[option.colorOptionId].upCharge"
												class="upcharge-watermark"
											>
												$
											</div>
										</span>
										<div class="form-input">
											<v-combobox
												dense
												outlined
												hide-details="auto"
												v-model="selected[option.colorOptionId]"
												:search-input.sync="colorSearchInputs[option.colorOptionId]"
												:items="option.items"
												item-text="label"
												item-value="colorId"
												:return-object="true"
												:menu-props="{ closeOnContentClick: true }"
												:ref="`colorInput-${indexOption}`"
												@keydown.enter.prevent="handleColorSearchKeydown($event, option, indexOption)"
												@keydown.tab.prevent="handleColorSearchKeydown($event, option, indexOption)"
												@change="handleColorChange($event, option, indexOption)"
											>
												<template v-slot:item="{ item, attrs, on }">
													<v-list-item
														v-on="on"
														v-bind="attrs"
														@click="
															localColorOptions[indexOption + 1]
																? (activeColorOption = JSON.parse(
																		JSON.stringify(localColorOptions[indexOption + 1])
																  ))
																: ''
														"
													>
														<v-list-item-content>
															<v-list-item-title>
																<v-row no-gutters align="center">
																	<ColorDisplay
																		:hex-code="item.hexCode"
																		:label="item.label"
																		:is-custom-color="item.isCustomColor"
																		shape="circle"
																		size="small"
																		class="color-list-item-preview-2"
																	/>
																	<span>{{ `${item.label} - ${item.description}` }}</span>
																</v-row>
															</v-list-item-title>
														</v-list-item-content>
													</v-list-item>
												</template>
											</v-combobox>
										</div>
									</template>
									<template v-else
										><p>
											{{
												option?.description ? `${option.name} - ${option.description}` : option.name
											}}
										</p>
										<div class="form-input">
											<v-text-field
												dense
												outlined
												hide-details="auto"
												v-model="selected[option.colorOptionId].text"
												@input="
													selected[option.colorOptionId].colorOptionId = option.colorOptionId;
													selected[option.colorOptionId].colorId = 0;
												"
												:ref="`colorInput-${indexOption}`"
											></v-text-field>
										</div>
									</template>
								</li>
								<!-- option selection for lettering only (systemCategoryId = 14) -->
								<template v-if="(type && type.toLowerCase() == 'lettering') || requiresLettering">
									<li class="color-list-item-letters">
										<p>Position of Letters</p>

										<div class="form-input">
											<v-select
												dense
												outlined
												hide-details="auto"
												v-model="positionOfLetters"
												:items="letterPositions"
												item-text="code"
												item-value="letterPositionId"
												ref="positionOfLettersInput"
												@keydown.tab="handlePositionOfLettersTab"
											>
												<template v-slot:item="{ item, attrs, on }">
													<v-list-item v-on="on" v-bind="attrs">
														<v-list-item-content>
															<v-list-item-title>
																<v-row no-gutters align="center">
																	<span>{{ `${item.code} - ${item.description}` }}</span>
																</v-row>
															</v-list-item-title>
														</v-list-item-content>
													</v-list-item>
												</template>
											</v-select>
										</div>
									</li>
									<li class="color-list-item-letters">
										<p>Text of Letters</p>

										<div class="form-input">
											<v-text-field
												dense
												outlined
												hide-details="auto"
												v-model="letters"
												ref="textOfLettersInput"
												@keydown.tab="handleTextOfLettersTab"
											></v-text-field>
										</div>
									</li>
								</template>
							</ul>
						</v-col>
						<v-col cols="8">
							<div class="color-display">
								<template v-for="(option, indexOption) in localColorOptions">
									<template
										v-if="
											!activeColorOption || activeColorOption.colorOptionId == option.colorOptionId
										"
									>
										<div
											v-for="(color, i) in option.items.filter(
												(c) =>
													(c.isRetired === false && c.isVisible !== false) ||
													selected[option.colorOptionId]?.colorId === c.colorId
											)"
											:key="`${option.colorOptionId}_${i}`"
											class="color-display-option"
											:class="{ active: selected[option.colorOptionId]?.colorId === color.colorId }"
											@click="
												selected[option.colorOptionId] = color;
												localColorOptions[indexOption + 1]
													? (activeColorOption = JSON.parse(
															JSON.stringify(localColorOptions[indexOption + 1])
													  ))
													: '';
											"
										>
											<ColorDisplay :hex-code="color.hexCode" :label="color.label" :is-custom-color="color.isCustomColor" shape="circle" size="xl">
												<div v-if="color.upCharge" class="upcharge-watermark">$</div>
											</ColorDisplay>
											<p class="color-display-tooltip">
												{{ color.label }} - {{ color.description }}
												<template v-if="color.upCharge"
													>({{ formatter.format(color.upCharge) }})</template
												>
											</p>
										</div>
									</template>
								</template>
							</div>
						</v-col>
					</v-row>
				</div>
			</div>
		</div>
	</v-overlay>
</template>

<script>
import gql from "graphql-tag";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import ColorDisplay from "@/components/ColorDisplay.vue";

export default {
	name: "ColorSelectionMenu",
	props: {
		modal: {
			type: Boolean,
			default: true,
		},
		type: {
			type: String,
			default: "",
		},
		colorOptions: {
			type: Array,
			default() {
				return [];
			},
		},
		colorSelectionData: {
			type: Array,
			default() {
				return [];
			},
		},
		textSelectionData: {
			type: Array,
			default() {
				return [];
			},
		},
		letterPositionId: {
			type: Number,
			default: null,
		},
		description: {
			type: String,
			default: null,
		},
		customText: {
			type: String,
			default: "",
		},
		matchPreviousPoNumber: {
			type: String,
			default: null,
		},
		itemId: {
			type: Number,
			default: null,
		},
		systemCategoryId: {
			type: Number,
			default: null,
		},
		styleCode: {
			type: String,
			default: null,
		},
		requiresLettering: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		SystemCategoryIcon,
		ColorDisplay,
	},
	data() {
		return {
			//strip reactivity from the passed in colorOptions so changes to delegate to parent automatically
			activeColorOption: null,
			selected: this.colorOptions.reduce(
				(acc, cur) => ((acc[cur.colorOptionId] = { colorId: "", label: "", text: "", upCharge: 0 }), acc),
				{}
			),
			colorSearchInputs: this.colorOptions.reduce(
				(acc, cur) => ((acc[cur.colorOptionId] = ""), acc),
				{}
			),
			previousPO: this.matchPreviousPoNumber,
			positionOfLetters: this.letterPositionId,
			letters: this.customText,
			letterPositions: [],
			formatter: new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
				minimumFractionDigits: 2,
			}),
			// Track timeouts for proper cleanup and race condition prevention
			focusTimeout: null,
			// Last PO number auto-applied, so the exact-match watchers don't re-apply
			// the same PO on every reactive tick. Seeded with the line's EXISTING match
			// number: opening the dialog must never re-apply the previous PO's values
			// over whatever is currently saved on the line — only a newly entered
			// number triggers the auto-apply.
			lastAppliedPo: this.matchPreviousPoNumber || null,
		};
	},
	watch: {
		// Pasting a FULL PO number defeats the combobox @input path: v-model and
		// search-input share `previousPO`, so selecting the single result sets a value
		// the model already holds — no change event, no auto-apply. Instead, apply as
		// soon as the typed/pasted text exactly matches a loaded PO. purchaseOrders is
		// watched too because the lookup results arrive async, after the text did.
		previousPO() {
			this.autoApplyExactMatch();
		},
		purchaseOrders() {
			this.autoApplyExactMatch();
		},
	},
	apollo: {
		letterPositions: {
			query() {
				return gql`
					query GetLetterPosition {
						letterPosition(skip: 0, take: 50, order: { code: ASC }) {
							items {
								letterPositionId
								code
								description
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			update(data) {
				return data.letterPosition.items;
			},
		},
		purchaseOrders: {
			query() {
				// Use different queries based on whether we have an inventoryItemId
				if (this.itemId) {
					return gql`
						query GetPurchaseOrders($orderNumber: String, $inventoryItemId: Int) {
							purchaseOrders(
								where: {
									purchaseOrderNumber: { contains: $orderNumber }
									id: { neq: $inventoryItemId }
								}
							) {
							items {
								id
								processTypeId
								purchaseOrderNumber
								vendorId
								vendor {
									companyName
								}
								vendorContactId
								salesOrderId
								dropShip
								customerAddressId
								shippingMethodId
								purchaseOrderDate
								expectedDate
								modifiedAt
								createdAt
								modifiedBy
								createdBy
								isCancelled
								customerAddress {
									addressId
									addressLine2
									addressLine1
								}
								notes {
									id
									noteText
									noteType
									createdBy
									modifiedBy
									modifiedAt
									createdAt
									purchaseOrderId
								}
								receivedItems {
									id
									receivedDate
									quantityReceived
								}
								orderItems {
									salesOrderLineItemId
									quantity
									unitPrice
									totalSizes
									hasSizes
									matchPreviousPoNumber
									note
									braidPosition
									itemOrdered {
										systemCategory
										inventoryItemId
										styleCode
										description
										processType
										processTypeId
									}
									sizes {
										size
										length
										sizeId
										lengthId
										quantity
									}
									specialSizes {
										length
										quantity
										size
									}
									colors {
										colorOption
										description
										color
										colorId
									}
									freeTextSelections {
										colorOptionId
										text
									}
									lettering {
										letterPositionId
										text
									}
									letteringText
									subItems {
										salesOrderLineItemId
										matchPreviousPoNumber
										note
										braidPosition
										colors {
											colorOption
											description
											color
											colorId
										}
										itemOrdered {
											systemCategory
											inventoryItemId
											styleCode
											description
											processType
											processTypeId
										}
										lettering {
											letterPositionId
											text
										}
										letteringText
									}
								}
							}
						}
					}
				`;
				} else {
					// Query without the id filter when itemId is null
					return gql`
						query GetPurchaseOrders($orderNumber: String) {
							purchaseOrders(
								where: {
									purchaseOrderNumber: { contains: $orderNumber }
								}
							) {
								items {
									id
									processTypeId
									purchaseOrderNumber
									vendorId
									vendor {
										companyName
									}
									vendorContactId
									salesOrderId
									dropShip
									customerAddressId
									shippingMethodId
									purchaseOrderDate
									expectedDate
									modifiedAt
									createdAt
									modifiedBy
									createdBy
									isCancelled
									customerAddress {
										addressId
										addressLine2
										addressLine1
									}
									notes {
										id
										noteText
										noteType
										createdBy
										modifiedBy
										modifiedAt
										createdAt
										purchaseOrderId
									}
									receivedItems {
										id
										receivedDate
										quantityReceived
									}
									orderItems {
										salesOrderLineItemId
										quantity
										unitPrice
										totalSizes
										hasSizes
										matchPreviousPoNumber
										note
										braidPosition
										itemOrdered {
											systemCategory
											inventoryItemId
											styleCode
											description
											processType
											processTypeId
										}
										sizes {
											size
											length
											sizeId
											lengthId
											quantity
										}
										specialSizes {
											length
											quantity
											size
										}
										colors {
											colorOption
											description
											color
											colorId
										}
										freeTextSelections {
											colorOptionId
											text
										}
										lettering {
											letterPositionId
											text
										}
										letteringText
										subItems {
											salesOrderLineItemId
											matchPreviousPoNumber
											note
											braidPosition
											colors {
												colorOption
												description
												color
												colorId
											}
											itemOrdered {
												systemCategory
												inventoryItemId
												styleCode
												description
												processType
												processTypeId
											}
											lettering {
												letterPositionId
												text
											}
											letteringText
										}
									}
								}
							}
						}
					`;
				}
			},
			variables() {
				// Only include inventoryItemId when it exists to match the query signature
				if (this.itemId) {
					return {
						inventoryItemId: this.itemId,
						orderNumber: this.previousPO ?? "",
					};
				} else {
					return {
						orderNumber: this.previousPO ?? "",
					};
				}
			},
			update(data) {
				return data.purchaseOrders.items.filter((po) => po.orderItems.length > 0);
			},
		},
	},
	methods: {
		// Helper for development-only logging
		debugLog(...args) {
			if (process.env.NODE_ENV === 'development') {
				console.log(...args);
			}
		},

		handleColorSearchKeydown(event, option, indexOption) {
		// Get the current search input value
		const searchValue = this.colorSearchInputs[option.colorOptionId];

		this.debugLog('=== handleColorSearchKeydown called ===', {
			eventKey: event.key,
			eventType: event.type,
			searchValue,
			optionId: option.colorOptionId,
			indexOption,
			defaultPrevented: event.defaultPrevented,
			propagationStopped: event.cancelBubble
		});

		// If no search value, manually handle Tab to move to next field
		if (!searchValue || searchValue.trim().length === 0) {
			this.debugLog('No search value, handling Tab navigation');
			if (event.key === 'Tab') {
				this.focusNextColorOrSave(indexOption);
			}
			return;
		}

		// Filter items based on search input to match v-combobox's filtering
		// v-combobox uses item-text which is "label" property
		const filteredItems = option.items.filter(item => {
			const itemLabel = (item.label || '').toLowerCase();
			const search = searchValue.toLowerCase();
			return itemLabel.includes(search);
		});

		this.debugLog('Filtered items:', {
			count: filteredItems.length,
			items: filteredItems.map(i => ({ label: i.label, colorId: i.colorId }))
		});

		// Only proceed if we have exactly one result
		if (filteredItems.length === 1) {
			const singleResult = filteredItems[0];
			this.debugLog('BEFORE auto-selection:', {
				eventKey: event.key,
				singleResult: { label: singleResult.label, colorId: singleResult.colorId },
				currentSelected: this.selected[option.colorOptionId],
				currentSearchInput: this.colorSearchInputs[option.colorOptionId]
			});

			// Set the selected value immediately (synchronously)
			this.$set(this.selected, option.colorOptionId, singleResult);
			this.debugLog('AFTER $set selected:', {
				eventKey: event.key,
				newSelected: this.selected[option.colorOptionId]
			});

			// Set the search input to null to let v-combobox display the selected item's label
			this.$set(this.colorSearchInputs, option.colorOptionId, null);
			this.debugLog('AFTER $set searchInput to null:', {
				eventKey: event.key,
				newSearchInput: this.colorSearchInputs[option.colorOptionId]
			});

			// Use nextTick to handle UI updates and navigation after selection completes
			this.$nextTick(() => {
				this.debugLog('INSIDE first $nextTick:', {
					eventKey: event.key,
					timestamp: Date.now()
				});

				// CRITICAL FIX for Tab key: Manually update v-combobox's internal values INSIDE $nextTick
				// This must happen AFTER Vue's reactive system processes our $set calls
				const currentRef = `colorInput-${indexOption}`;
				if (this.$refs[currentRef] && this.$refs[currentRef][0]) {
					const combobox = this.$refs[currentRef][0];
					combobox.internalValue = singleResult;
					combobox.lazyValue = singleResult;
					this.debugLog('Manually set combobox internal values (in first $nextTick):', {
						eventKey: event.key,
						internalValue: combobox.internalValue,
						lazyValue: combobox.lazyValue
					});

					// Use a second $nextTick to ensure the values stick before closing/blurring
					this.$nextTick(() => {
						this.debugLog('INSIDE second $nextTick (before close/blur):', {
							eventKey: event.key,
							timestamp: Date.now()
						});

						this.debugLog('Combobox state BEFORE close/blur:', {
							eventKey: event.key,
							isMenuActive: combobox.isMenuActive,
							isFocused: combobox.isFocused,
							internalValue: combobox.internalValue,
							lazyValue: combobox.lazyValue,
							hasBlurMethod: typeof combobox.blur === 'function'
						});

						// Close the menu
						if (combobox.isMenuActive) {
							this.debugLog('Closing menu for key:', event.key);
							combobox.isMenuActive = false;
						}

						// Blur the component
						if (combobox.blur) {
							this.debugLog('Calling blur() for key:', event.key);
							combobox.blur();
						}

						// Check state after
						this.$nextTick(() => {
							this.debugLog('Combobox state AFTER close/blur:', {
								eventKey: event.key,
								isMenuActive: combobox.isMenuActive,
								isFocused: combobox.isFocused,
								internalValue: combobox.internalValue,
								lazyValue: combobox.lazyValue
							});
						});

						// Move to next color option or save button
						this.debugLog('Calling focusNextColorOrSave for key:', event.key);
						this.focusNextColorOrSave(indexOption);
					});
				}
			});
		} else if (filteredItems.length > 1 && event.key === 'Tab') {
			// Multiple results and Tab pressed - auto-select the first filtered item
			this.debugLog('Multiple results, Tab pressed - selecting first item:', {
				count: filteredItems.length,
				firstItem: { label: filteredItems[0].label, colorId: filteredItems[0].colorId }
			});

			const firstResult = filteredItems[0];

			// Set the selected value
			this.$set(this.selected, option.colorOptionId, firstResult);

			// Clear the search input to show the selected item's label
			this.$set(this.colorSearchInputs, option.colorOptionId, null);

			this.$nextTick(() => {
				// Update v-combobox's internal values
				const currentRef = `colorInput-${indexOption}`;
				if (this.$refs[currentRef] && this.$refs[currentRef][0]) {
					const combobox = this.$refs[currentRef][0];
					combobox.internalValue = firstResult;
					combobox.lazyValue = firstResult;

					this.$nextTick(() => {
						// Close the menu
						if (combobox.isMenuActive) {
							combobox.isMenuActive = false;
						}

						// Blur the component
						if (combobox.blur) {
							combobox.blur();
						}

						// Move to next color option or save button
						this.focusNextColorOrSave(indexOption);
					});
				}
			});
		} else {
			// No results found and Tab was pressed, manually move focus
			this.debugLog('No matching results, count:', filteredItems.length);
			if (event.key === 'Tab') {
				this.focusNextColorOrSave(indexOption);
			}
		}
	},

	handleColorChange(selectedValue, option, indexOption) {
		// Only auto-focus if a valid color object was selected (not empty string or null)
		if (selectedValue && typeof selectedValue === 'object' && selectedValue.colorId) {
			// Use $nextTick to ensure the selection is fully processed
			this.$nextTick(() => {
				// Auto-focus the next color input or lettering field or save button
				this.focusNextColorOrSave(indexOption);
			});
		}
	},

	handlePositionOfLettersTab(event) {
		// Prevent default Tab behavior
		event.preventDefault();

		// Focus the Text of Letters input
		this.$nextTick(() => {
			if (this.$refs.textOfLettersInput && this.$refs.textOfLettersInput.$el) {
				const textInput = this.$refs.textOfLettersInput.$el.querySelector('input');
				if (textInput) {
					textInput.focus();
				}
			}
		});
	},

	handleTextOfLettersTab(event) {
		// Prevent default Tab behavior
		event.preventDefault();

		// Focus the Save button
		this.$nextTick(() => {
			if (this.$refs.saveButton && this.$refs.saveButton.$el) {
				this.$refs.saveButton.$el.focus();
			}
		});
	},

	focusNextColorOrSave(currentIndex) {
		// Clear any pending focus timeout to prevent race conditions
		if (this.focusTimeout) {
			clearTimeout(this.focusTimeout);
			this.focusTimeout = null;
		}

		// Check if there's a next color option
		if (this.localColorOptions[currentIndex + 1]) {
			this.activeColorOption = this.localColorOptions[currentIndex + 1];

			// Use $nextTick instead of arbitrary setTimeout for DOM updates
			this.$nextTick(() => {
				const nextRef = `colorInput-${currentIndex + 1}`;
				if (this.$refs[nextRef] && this.$refs[nextRef][0]) {
					const inputEl = this.$refs[nextRef][0].$el.querySelector('input');
					if (inputEl) {
						inputEl.focus();
					}
				}
			});
		} else {
			// No more color options - check for lettering fields before Save button
			this.$nextTick(() => {
				// Check if lettering is enabled and Position of Letters field exists
				if (((this.type && this.type.toLowerCase() === 'lettering') || this.requiresLettering) &&
				    this.$refs.positionOfLettersInput) {
					// Focus Position of Letters dropdown
					if (this.$refs.positionOfLettersInput.$el) {
						const selectInput = this.$refs.positionOfLettersInput.$el.querySelector('input');
						if (selectInput) {
							selectInput.focus();
							return;
						}
					}
				}

				// If no lettering or already filled, focus the Save button
				if (this.$refs.saveButton && this.$refs.saveButton.$el) {
					this.$refs.saveButton.$el.focus();
				}
			});
		}
	},

		autoApplyExactMatch() {
			const value = this.previousPO;
			if (!value || value === this.lastAppliedPo) return;
			const match = (this.purchaseOrders || []).find(
				(po) => po.purchaseOrderNumber === value
			);
			if (match) {
				this.applyColors(value);
			}
		},

		applyColors(orderNumber) {
			let purchaseOrder = this.purchaseOrders.find((po) => po.purchaseOrderNumber === orderNumber);

			if (!purchaseOrder) {
				console.warn("purchaseOrder is not found");
				return;
			}

			this.lastAppliedPo = orderNumber;

			let orderItem = purchaseOrder.orderItems[0];

			if (!this.localColorOptions || !Array.isArray(this.localColorOptions)) {
				console.warn("localColorOptions is not an array or is undefined");
				return;
			}

			if (!orderItem || !orderItem.colors || !Array.isArray(orderItem.colors)) {
				console.warn("orderItem.colors is not an array or is undefined");
				return;
			}

			for (let [index, option] of this.localColorOptions.entries()) {
				if (!option || !option.colorOptionId || !Array.isArray(option.items)) {
					console.warn(`Invalid option at index ${index}`);
					continue;
				}

				const correspondingColor = orderItem.colors[index];
				if (!correspondingColor || typeof correspondingColor.colorId === "undefined") {
					console.warn(`No corresponding color found for index ${index}`);
					continue;
				}

				const foundItem = option.items.find((c) => c.colorId === correspondingColor.colorId);
				if (foundItem) {
					// apply selection
					this.selected[option.colorOptionId] = foundItem;
				} else {
					// handle the case where no item is found
					console.warn(
						`No item found with colorId ${correspondingColor.colorId} at index ${index}`
					);
				}
			}

			if (orderItem.letteringText) {
				this.letters = orderItem.letteringText;
			}

			if (orderItem.lettering) {
				this.positionOfLetters = orderItem.lettering.letterPositionId;
			}

			// Free-text options (DC - Design Code, Size - Overall Size, ...) must carry
			// over too (#1461) — matched by colorOptionId, so a PO from the same item
			// maps exactly and unrelated options are skipped silently.
			for (const ft of orderItem.freeTextSelections || []) {
				const entry = this.selected[ft.colorOptionId];
				if (entry && ft.text) {
					entry.text = ft.text;
					entry.colorOptionId = ft.colorOptionId;
					entry.colorId = 0;
				}
			}
		},

		formatPreviousPO() {
			if (this.matchPreviousPoNumber?.length == 0) return null;
			else return this.matchPreviousPoNumber;
		},
		saveColorOptions() {
			let letterPositionById = this.letterPositions.find(
				(s) => s.letterPositionId == this.positionOfLetters
			);

			let colorData = {
				colorSelections: Object.values(this.selected).filter(
					(s) => (s.colorId && s.colorId !== "") || (s.text && s.text !== "")
				),
				letterPositionId: this.positionOfLetters,
				description: letterPositionById ? letterPositionById.description : null,
				customText: this.letters,
				matchPreviousPoNumber: this.previousPO?.length === 0 ? null : this.previousPO,
			};

			//send a colorData obj back to parent
			this.$emit("save", colorData);
		},
	},
	computed: {
		isContentHidden() {
			// Always show the color options content
			// The previous logic was hiding content while typing in the PO search,
			// which created a jarring user experience
			return true;
		},
		localColorOptions() {
			return this.colorOptions?.map((option) => {
				// Clone the option to avoid mutating the original object
				let newOption = JSON.parse(JSON.stringify(option));

				// Check if colorPalettes exists before trying to map over it
				if (newOption.colorPalettes && Array.isArray(newOption.colorPalettes)) {
					// Map over each colorPalette to include upcharge in its colors
					newOption.colorPalettes = newOption.colorPalettes.map((palette) => {
						// Check if the palette has an upcharge
						let upcharge = palette.upcharge;

						// If there's an upcharge, add it to each color in the palette
						if (upcharge !== null && palette.colors && Array.isArray(palette.colors)) {
							palette.colors = palette.colors.map((color) => ({
								...color,
								upCharge: upcharge,
							}));
						}
						return palette;
					});

					// Aggregate colors from all palettes while including the upcharge data
					newOption.items = newOption.colorPalettes.flatMap((palette) => palette.colors || []);
				} else {
					// If no colorPalettes, ensure items is an empty array
					newOption.colorPalettes = [];
					newOption.items = [];
				}

				return newOption;
			});
		},
		computedTotalColorOptions() {
			let colorOptions = [];

			//loop through every options color palettes and retrive the individual colors
			this.localColorOptions.forEach((option) => {
				if (option.colorPalettes && option.colorPalettes.length > 0) {
					option.colorPalettes.forEach((palette) => {
						colorOptions = [...colorOptions, ...palette.colors];
					});
				}
			});

			//remove any duplicate colors from the colorOptions array
			colorOptions = [...new Map(colorOptions.map((color) => [color.hexCode, color])).values()];

			return colorOptions;
		},
		filteredColorOptions() {
			return this.localColorOptions?.map((option) => {
				let newOption = { ...option };
				newOption.items = option.items
					.map((color) => {
						// Find the palette that the color belongs to
						const palette = option.colorPalettes.find((palette) =>
							palette.colors.some((pColor) => pColor.colorId === color.colorId)
						);

						// If a matching palette is found and it has an upcharge, add it to the color item
						if (palette && palette.upcharge !== null) {
							return { ...color, upCharge: palette.upcharge };
						}

						// If no matching palette with an upcharge is found, return the color item unchanged
						return color;
					})
					.filter((color) => {
						// Allow if this color is already selected (preserve existing selections)
						if (
							this.selected[option.colorOptionId] &&
							this.selected[option.colorOptionId].colorId === color.colorId
						) {
							return true;
						}
						// Filter out retired colors and colors marked as not visible
						if (!color.isRetired && color.isVisible !== false) {
							return true;
						}
						return false;
					});
				return newOption;
			});
		},
	},
	mounted() {
		if (this.filteredColorOptions.length) this.activeColorOption = this.filteredColorOptions[0];
		if (this.colorSelectionData.length > 0 || this.textSelectionData.length > 0) {
			const initialSelected = {};
			const selectedColorIds = this.colorSelectionData.map((el) => {
				return { colorId: el.colorId, id: el.id };
			});
			this.localColorOptions.forEach((option) => {
				if (option?.type && option.type.toLowerCase() === "color") {
					for (const item of option.items) {
						const cid = selectedColorIds.find((cid) => cid.colorId === item.colorId);
						if (cid) {
							initialSelected[option.colorOptionId] = { ...item, ...cid };
							break; // Break the loop when the item is found
						}
					}
				}

				if (option?.type && option.type.toLowerCase() === "freetext") {
					let selection = this.colorSelectionData.find(
						(s) => s.colorOptionId === option.colorOptionId
					);

					if (!selection) {
						// fallback to pull from text selection data
						selection = this.textSelectionData.find(
							(s) => s.colorOptionId === option.colorOptionId
						);
					}

					if (selection) {
						// Clone — assigning the parent's object by reference let every
						// keystroke mutate the line item's real data, so Cancel could
						// never roll anything back. Changes must only reach the parent
						// through the save event.
						initialSelected[option.colorOptionId] = { ...selection };
					}
				}
			});
			this.selected = { ...this.selected, ...initialSelected };
		}

		// Focus on the first color option input field when component is mounted
		this.$nextTick(() => {
			this.$nextTick(() => {
				// Check if first option has a value
				const firstOption = this.filteredColorOptions[0];
				if (!firstOption) return;

				const hasValue =
					this.selected[firstOption.colorOptionId] &&
					(this.selected[firstOption.colorOptionId].colorId ||
						this.selected[firstOption.colorOptionId].text);

				// Only focus and open dropdown if no value exists
				if (!hasValue) {
					const firstInput = this.$refs[`colorInput-0`]?.[0];
					if (firstInput) {
						// First focus the input
						firstInput.focus();

						// Then add a small delay before opening the dropdown menu
						setTimeout(() => {
							// Programmatically open the dropdown menu
							if (firstInput.isMenuActive !== undefined) {
								firstInput.isMenuActive = true;
							} else if (firstInput.$refs && firstInput.$refs.menu) {
								firstInput.$refs.menu.isActive = true;
							}
						}, 500);
					}
				}
			});
		});
	},
	beforeDestroy() {
		// Clean up any pending timeouts to prevent memory leaks and errors
		if (this.focusTimeout) {
			clearTimeout(this.focusTimeout);
			this.focusTimeout = null;
		}
	},
};
</script>

<style lang="scss" scoped>
.v-overlay {
	z-index: 6 !important;

	::v-deep {
		.v-overlay__content {
			width: 95%;
		}
	}

	&-relative {
		position: fixed;
		padding: 3rem 0;
		width: 100%;
	}
}

.input-modal {
	width: 100%;
	margin: auto;
	border-radius: 3px;
	overflow: hidden;
	box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;

	&-header {
		position: relative;
		display: flex;
		width: 100%;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: flex-end;
		background-color: $color-white;
		padding: 1rem;
		border-bottom: 1px solid $color-gray;

		&-title {
			font-size: 1.6rem;
			margin-right: auto;
		}

		.v-btn {
			margin-left: 1rem;
		}

		.form-input {
			margin: 0;
			flex-flow: row;
			align-items: center;

			label {
				width: initial;
				margin-right: 1rem;
			}
			.v-text-field {
				width: auto;
			}
		}
	}

	&-content {
		display: block;
		width: 100%;
		background-color: $color-white;
	}
}

.color {
	&-container {
		position: relative;
	}

	&-list {
		list-style: none;
		min-height: 40rem;
		overflow-y: scroll;
		padding: 0;

		&-item {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			padding: 1rem;
			border-bottom: 1px solid $color-gray;

			&.active {
				background-color: #f3f3f3;
			}

			&-preview {
				margin-left: auto;
				margin-right: 1rem;
				width: 4rem;
				height: 4rem;
				min-width: 4rem;
				min-height: 4rem;
				border: 1px solid $color-gray-light-2;
				border-radius: 50%;
				transition: all 0.3s;
				display: flex;
				justify-content: center;
				align-items: center;
				position: relative;
				overflow: hidden;

				&:hover {
					cursor: pointer;
					border: 1px solid $color-gray;
				}

				&-active {
					border: 2px solid $color-primary;

					&:hover {
						border: 2px solid $color-primary;
					}
				}

				&-2 {
					//color preview for combobox
					margin-right: 1rem;
					width: 2rem;
					height: 2rem;
					border: 1px solid $color-gray-light-2;
					border-radius: 25px;
				}
			}

			&-letters {
				display: flex;
				flex-flow: row nowrap;
				align-items: center;
				padding: 1rem;
				border-bottom: 1px solid $color-gray;

				.form-input {
					margin-left: auto;
				}
			}
		}
	}

	&-display {
		display: flex;
		flex-flow: row wrap;
		justify-content: center;
		align-items: center;
		height: 100%;
		padding: 2rem;

		&-option {
			position: relative;
			display: flex;
			flex-flow: column nowrap;
			justify-content: center;
			align-items: center;
			margin: 1rem;
			text-align: center;
			padding: 2px;
			border-radius: 50%;
			border: 1px solid transparent;

			&.active {
				border: 1px solid red;
			}
			&:hover .color-display-tooltip {
				visibility: visible;
				opacity: 1;
			}

			span {
				position: relative;
				display: flex;
				width: 6rem;
				height: 6rem;
				border: 1px solid $color-gray-light-2;
				border-radius: 30px;
				justify-content: center;
				align-items: center;
			}

			&-2 {
				position: relative;
				display: flex;
				flex-flow: column nowrap;
				justify-content: center;
				align-items: center;
				margin: 1rem;
				text-align: center;

				&:hover .color-display-tooltip {
					visibility: visible;
					opacity: 1;
				}

				span {
					position: relative;
					display: block;
					width: 6rem;
					height: 6rem;
					border: 1px solid $color-gray-light-2;
					border-radius: 30px;
					margin-bottom: 0.8rem;

					&:hover {
						cursor: pointer;
						border: 1px solid $color-gray-dark;
					}
				}
			}
		}

		&-tooltip {
			visibility: hidden;
			opacity: 0;
			position: absolute;
			top: 105%;
			border-radius: 5px;
			padding: 0.5rem 0.8rem;
			width: 15rem;
			background-color: $color-gray-dark;
			font-size: 1.2rem;
			color: $color-white;
			text-align: center;
			z-index: 2;
			transition: all 0.4s ease;

			&::after {
				content: "";
				position: absolute;
				bottom: 100%;
				left: 50%;
				margin-left: -5px;
				border-width: 5px;
				border-style: solid;
				border-color: transparent transparent $color-gray-dark transparent;
			}
		}
	}
}
</style>

<style lang="scss">
.upcharge-watermark {
	position: absolute;
	color: #e0e0e0;
	font-size: 1.2em;
	opacity: 0.7;
	pointer-events: none;
}
</style>
